import { useMutation } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { type ErrorWithBody, SignUpUrl, post, queryKeys } from '@mobi/account/Utils'
import type { AccountHolderAddress, ApplicationResponse } from '@mobi/api-types'
import { type DuplicateContactCheckResponse } from '../../PersonalDetails/Hooks'
import { getSignUpState } from '../Store'

export type TypeSignupApplication = {
  firstName: string
  middleName: string
  surname: string
  birthdate: string
  email: string
  mobileNumber: string
  applicationReferenceNumber: number
  enquiryId: string
  residentialAddress?: AccountHolderAddress
  previousAddress?: AccountHolderAddress
  marketingOptInIndicator: boolean
  termsAndConditionsText?: string
  termsAndConditionsConsent?: boolean
  firstNameAlias: string
  lastNameAlias: string
}

export type TypeSignUpApplicationRequest = TypeSignupApplication & {
  affiliateMarketingId?: string
  locationId?: string
  venueId?: string
}

export type TypeSignUpApplicationResponse = {
  applicationReferenceNumber: number
  duplicateExists: boolean
  isDuplicateClosed: boolean
  isVerified: boolean
  unVerifiedReason: string
}

type UseSignUpApplicationOptions = {
  setValidationMessages: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setShowErrorPopup: React.Dispatch<React.SetStateAction<boolean>>
}

export const useSignUpApplication = ({
  setValidationMessages,
  setShowErrorPopup,
}: UseSignUpApplicationOptions) => {
  const signUpState = useSelector(getSignUpState)
  const { mutate: saveSignUpApplication, status: saveSignUpApplicationStatus } = useMutation<
    TypeSignUpApplicationResponse | ApplicationResponse,
    ErrorWithBody<DuplicateContactCheckResponse>,
    TypeSignUpApplicationRequest
  >({
    mutationFn: applicationDetails =>
      post<
        TypeSignUpApplicationRequest,
        TypeSignUpApplicationResponse | ApplicationResponse,
        ErrorWithBody<DuplicateContactCheckResponse>
      >(SignUpUrl.signUpApplication, applicationDetails),
    mutationKey: queryKeys.signupApplicationSave,
    onError: error => {
      if (error.status === 409 && error.body) {
        if (error.body.isDuplicateMobile) {
          signupTracker({
            interaction_type: 'Sign Up Error',
            signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
            message: 'Duplicate Mobile Number',
          })
          setValidationMessages(prev => ({ ...prev, mobile: 'duplicate' }))
        }
        if (error.body.isDuplicateEmail) {
          signupTracker({
            interaction_type: 'Sign Up Error',
            signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
            message: 'Duplicate Email',
          })
          setValidationMessages(prev => ({ ...prev, email: 'duplicate' }))
        }
        return
      }
      setShowErrorPopup(true)
      signupTracker({
        interaction_type: 'Sign Up Error',
        signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
        message: error.statusText || 'Server Error',
      })
    },
  })
  return { saveSignUpApplication, saveSignUpApplicationStatus }
}
